import { FormControlLabel, Icon, Switch, Tooltip } from "@mui/material";
import Loader from "examples/Loader";
import { useContext, useEffect, useState } from "react";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import {
  useDeleteSaasConfiguration,
  useToggleSaasAccountLevel,
  useUpdateSaasById,
} from "queries/saas";
import { useAgencyInfo } from "context/agency";
import axios from "axios";
import DeleteModal from "./components/DeleteModal";


const NormalTable = ({ data, openeditsaasmodel }) => {
  const [agency] = useAgencyInfo();
  const [isCopy, setIsCopy] = useState({ id: "", type: "" });
  const [openSoftModel, setOpenSoftModel] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Counts any in-flight query whose key starts with "saas" (the shared
  // prefix every mutation in queries/saas.js invalidates), regardless of
  // which component actually owns that useQuery call - so this catches the
  // background refetch right after any saas edit/delete/toggle, not just
  // this table's own initial load.
  const isSaasFetching = useIsFetching({ queryKey: ["saas"] }) > 0;

  const handleCopyLink = (id, type) => {
    try {
      let link = window.location.hostname.includes("localhost")
        ? "http://localhost:4042"
        : agency?.domain
          ? agency.domain
          : "https://app.trustbrand.ai";

      link += `/auth/sign-up?plan=${id}&type=${type}`;

      const textArea = document.createElement("textarea");
      textArea.value = link;

      // Prevent scrolling/jumping
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.opacity = "0";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      if (successful) {
        setIsCopy({ id, type });
        console.log("Link copied!");
      } else {
        console.log("Copy failed.");
      }

      document.body.removeChild(textArea);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const currencySymbols = [
    {
      label: "usd",
      value: "$",
    },
    {
      label: "eur",
      value: "€",
    },
    {
      label: "jpy",
      value: "¥",
    },
    {
      label: "gbp",
      value: "£",
    },
    {
      label: "aud",
      value: "A$",
    },
    {
      label: "cad",
      value: "C$",
    },
    {
      label: "chf",
      value: "Fr",
    },
    {
      label: "cny",
      value: "¥",
    },
    {
      label: "sek",
      value: "kr",
    },
    {
      label: "nzd",
      value: "NZ$",
    },
    {
      label: "krw",
      value: "₩",
    },
    {
      label: "sgd",
      value: "S$",
    },
    {
      label: "nok",
      value: "kr",
    },
    {
      label: "mxn",
      value: "Mex$",
    },
    {
      label: "inr",
      value: "₹",
    },
    {
      label: "rub",
      value: "₽",
    },
    {
      label: "zar",
      value: "R",
    },
    {
      label: "try",
      value: "₺",
    },
    {
      label: "brl",
      value: "R$",
    },
    {
      label: "twd",
      value: "NT$",
    },
    {
      label: "dkk",
      value: "kr",
    },
    {
      label: "pln",
      value: "zł",
    },
    {
      label: "thb",
      value: "฿",
    },
    {
      label: "idr",
      value: "Rp",
    },
    {
      label: "huf",
      value: "Ft",
    },
    {
      label: "czk",
      value: "Kč",
    },
    {
      label: "ils",
      value: "₪",
    },
    {
      label: "clp",
      value: "CLP$",
    },
    {
      label: "php",
      value: "₱",
    },
    {
      label: "aed",
      value: "د.إ",
    },
    {
      label: "cop",
      value: "COL$",
    },
    {
      label: "sar",
      value: "﷼",
    },
    {
      label: "myr",
      value: "RM",
    },
    {
      label: "ron",
      value: "lei",
    },
    {
      label: "pkr",
      value: "₨",
    },
    {
      label: "vnd",
      value: "₫",
    },
    {
      label: "ngn",
      value: "₦",
    },
    {
      label: "egp",
      value: "E£",
    },
    {
      label: "bdt",
      value: "৳",
    },
    {
      label: "pyg",
      value: "₲",
    },
    {
      label: "irr",
      value: "﷼",
    },
    {
      label: "iqd",
      value: "ع.د",
    },
    {
      label: "dzd",
      value: "DA",
    },
    {
      label: "kwd",
      value: "ك",
    },
    {
      label: "uah",
      value: "₴",
    },
    {
      label: "hrk",
      value: "kn",
    },
    {
      label: "omr",
      value: "﷼",
    },
    {
      label: "tnd",
      value: "د.ت",
    },
    {
      label: "qar",
      value: "﷼",
    },
    {
      label: "crc",
      value: "₡",
    },
    {
      label: "ars",
      value: "AR$",
    },
    {
      label: "lkr",
      value: "₨",
    },
    {
      label: "vnd",
      value: "₫",
    },
    {
      label: "mad",
      value: "د.م.",
    },
    {
      label: "npr",
      value: "रू",
    },
    {
      label: "uzs",
      value: "сўм",
    },
    {
      label: "mvr",
      value: "ރ.",
    },
    {
      label: "jod",
      value: "د.ا",
    },
    {
      label: "bhd",
      value: "ب.د",
    },
    {
      label: "kzt",
      value: "₸",
    },
    {
      label: "byn",
      value: "Br",
    },
    {
      label: "lbp",
      value: "ل.ل.",
    },
    {
      label: "gtq",
      value: "Q",
    },
    {
      label: "ttd",
      value: "TT$",
    },
    {
      label: "svc",
      value: "₡",
    },
    {
      label: "afn",
      value: "؋",
    },
    {
      label: "yer",
      value: "﷼",
    },
    {
      label: "bob",
      value: "Bs",
    },
    {
      label: "bmd",
      value: "BD$",
    },
    {
      label: "bzd",
      value: "BZ$",
    },
    {
      label: "xcd",
      value: "EC$",
    },
    {
      label: "gel",
      value: "₾",
    },
    {
      label: "jmd",
      value: "J$",
    },
    {
      label: "kyd",
      value: "KY$",
    },
    {
      label: "mnt",
      value: "₮",
    },
    {
      label: "nad",
      value: "N$",
    },
    {
      label: "pab",
      value: "B/.",
    },
    {
      label: "sdg",
      value: "ج.س.",
    },
    {
      label: "sdp",
      value: "£S",
    },
    {
      label: "lvl",
      value: "Ls",
    },
    {
      label: "ltl",
      value: "Lt",
    },
    {
      label: "zwd",
      value: "Z$",
    },
    {
      label: "awg",
      value: "Afl.",
    },
    {
      label: "bbd",
      value: "Bds$",
    },
    {
      label: "bgn",
      value: "лв",
    },
    {
      label: "cdf",
      value: "FC",
    },
    {
      label: "xof",
      value: "CFA",
    },
    {
      label: "djf",
      value: "Fdj",
    },
    {
      label: "fkp",
      value: "FK£",
    },
    {
      label: "gip",
      value: "GIP",
    },
    {
      label: "gnf",
      value: "FG",
    },
    {
      label: "htg",
      value: "HTG",
    },
    {
      label: "iqd",
      value: "ع.د",
    },
    {
      label: "irr",
      value: "﷼",
    },
    {
      label: "isk",
      value: "kr",
    },
    {
      label: "lak",
      value: "₭",
    },
    {
      label: "lsl",
      value: "LSL",
    },
    {
      label: "mmk",
      value: "K",
    },
    {
      label: "mro",
      value: "UM",
    },
    {
      label: "mzn",
      value: "MT",
    },
    {
      label: "nio",
      value: "C$",
    },
    {
      label: "pgk",
      value: "PGK",
    },
    {
      label: "rwf",
      value: "R₣",
    },
    {
      label: "shp",
      value: "£",
    },
    {
      label: "std",
      value: "Db",
    },
    {
      label: "szl",
      value: "E",
    },
    {
      label: "top",
      value: "T$",
    },
    {
      label: "ugx",
      value: "USh",
    },
    {
      label: "vuv",
      value: "VT",
    },
    {
      label: "wst",
      value: "WS$",
    },
    {
      label: "xaf",
      value: "FCFA",
    },
    {
      label: "xpf",
      value: "F",
    },
    {
      label: "zmw",
      value: "ZK",
    },
  ];

  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of items per page

  // Filter data based on the search query - falls back to [] while `data`
  // isn't an array yet (initial load), since the actual loading check can't
  // run until every hook below has been called (see the early return after
  // the last hook, further down) - an early return here would skip them on
  // a later render once they've already run once, which is the "Rendered
  // fewer hooks than expected" crash.
  const filteredData = Array.isArray(data) ? data : [];

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const maxButtonsToShow = 5; // Number of pagination buttons to display at a time

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const [currentItems, setCurrentItems] = useState([]);

  useEffect(() => {
    setCurrentItems(filteredData.slice(indexOfFirstItem, indexOfLastItem));
  }, [data, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  function getCurrencySymbol(label) {
    const currency = currencySymbols.find((item) => item.label === label);
    return currency ? currency.value : null; // Return the symbol if found, else null
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteItem(item);
    setOpenSoftModel(true);
    setDeleteConfirmationText("");
  };

  const handleCloseModal = () => {
    setOpenSoftModel(false);
    setDeleteItem(null);
    setDeleteConfirmationText("");
  };

  const { mutateAsync: deleteSaasConfigration } = useDeleteSaasConfiguration();
  const deletesaasmodel = async () => {
    if (!deleteItem) return;

    setDeleteLoading(true);
    const payload = {
      _id: deleteItem?._id,
      agency_id: agency?._id,
      product_id: deleteItem?.product_id,
    };
    try {
      await deleteSaasConfigration(payload, {
        onSuccess: ({ response }) => {
          if (response) {
            handleCloseModal();
            queryClient.invalidateQueries({ queryKey: ["saas"] });
          }
        }
      });
    } finally {
      setDeleteLoading(false);
    }
  };



  const copyFields = (value) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        alert(`${value} Copied Successfully..!`);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };
  const { mutateAsync: toggleShowOnAccountLevel } = useToggleSaasAccountLevel();
  const { mutateAsync: updateSaasById } = useUpdateSaasById();

  // Every hook above has now run unconditionally on every render, so it's
  // safe to branch from here on.
  if (!Array.isArray(data) || isSaasFetching) {
    return <Loader />;
  }

  const handleToggleChange = async (item) => {
    // Optimistic Update: Update UI immediately
    const updatedItems = currentItems.map((currentItem) =>
      currentItem._id === item._id
        ? { ...currentItem, isenable_account: !item.isenable_account }
        : currentItem
    );
    setCurrentItems(updatedItems); // Assuming `setCurrentItems` manages the local state for `currentItems`

    // Prepare payload
    const payload = {
      _id: item?._id,
      agency_id: agency?._id,
      product_id: item?.product_id,
      isenable_account: !item?.isenable_account,
    };

    try {
      // Make the API call
      const { response } = await toggleShowOnAccountLevel(payload);

      if (!response) {
        // Rollback if API fails
        setCurrentItems(currentItems);
        console.error("Failed to update toggle on the server.");
      }
    } catch (error) {
      // Rollback in case of an error
      setCurrentItems(currentItems);
      console.error("Error updating toggle:", error);
    }
  };

  const updateOnSaasToggleChange = async (item) => {
    const updateItems = currentItems?.map((plan) =>
      plan._id === item._id
        ? { ...plan, developer_plan: !item.developer_plan }
        : plan
    )
    setCurrentItems(updateItems);

    const payload = {
      _id: item?._id,
      developer_plan: !item?.developer_plan,
    };

    try {
      const { response } = await updateSaasById(payload);

      if (!response) {
        setCurrentItems(currentItems);
        console.error("Failed to update toggle on the server.");
      }
    } catch (error) {
      setCurrentItems(currentItems);
      console.error("Error updating toggle:", error);
    }
  }

  const updateOnDefaultPlanToggleChange = async (item) => {
    const newValue = !item?.default_plan;
    // Only one plan can be the default at a time. Enabling one clears the rest.
    const updateItems = currentItems?.map((plan) =>
      plan._id === item._id
        ? { ...plan, default_plan: newValue }
        : { ...plan, default_plan: newValue ? false : plan.default_plan }
    )
    setCurrentItems(updateItems);

    const payload = {
      _id: item?._id,
      default_plan: newValue,
    };

    try {
      const { response } = await updateSaasById(payload);

      if (!response) {
        setCurrentItems(currentItems);
        console.error("Failed to update toggle on the server.");
      }
    } catch (error) {
      setCurrentItems(currentItems);
      console.error("Error updating toggle:", error);
    }
  }

  // Calculate start and end of pagination buttons
  const startPage = Math.max(currentPage - Math.floor(maxButtonsToShow / 2), 1);
  const endPage = Math.min(startPage + maxButtonsToShow - 1, totalPages);

  return (
    <>
      <div className="w-100">
        <div className="d-flex w-100">
          <p className="p-20-px w-12-per text-left ">Plan Name</p>
          <p className="p-20-px w-12-per text-left"></p>
          <p className="p-20-px w-12-per text-left">Feature</p>
          <p className="p-20-px w-12-per text-left">Credits</p>
          <p className="p-20-px w-12-per text-left">Annual</p>
          <p className="p-20-px w-12-per text-left">Monthly</p>
          <p className="p-20-px w-12-per text-left">Developer Plan</p>
          <p className="p-20-px w-12-per text-left">Plan on Account Level</p>
          <p className="p-20-px w-12-per text-left">Default Plan</p>

          <p className="p-20-px w-12-per text-left">Payment Link Monthly</p>
          <p className="p-20-px w-12-per text-left">Payment Link Annually</p>
          {/* <p className="p-20-px w-12-per text-left"></p> */}
          <p className="p-20-px w-12-per text-left"></p>
        </div>
        <div>
          {currentItems?.length > 0 &&
            currentItems?.map((item, index) => (
              <>
                <div className="d-flex w-100 tablebody-row" style={{ alignItems: "center" }}>
                  <div className="p-20-px text-black text-bold text-left w-12-per">
                    {" "}
                    {item.product_name}{" "}
                  </div>
                  <div className="p-20-px w-12-per text-left">
                    <span className="alert-default-new"> DEFAULT</span>
                  </div>
                  <div className="p-20-px w-12-per text-left">
                    <div className="d-flex justifu-content-between">
                      {(item?.apps?.[0]?.snapshotselected?.includes("AI Social App") || item?.apps?.includes("AI Social App")) && (
                        <div className="everyicon-z">
                          <div className="ml-2 mr-2 mb-2 ">
                            <div className="border-gray-300 border rounded-full h-9 w-9 p-2">
                              <svg
                                width="21"
                                height="18"
                                viewBox="0 0 21 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M9.625 4.37413C11.7206 5.01375 13.3612 6.65525 13.9991 8.75C14.6405 6.65438 16.2811 5.01463 18.375 4.375C16.2811 3.73537 14.6405 2.09562 13.9991 0C13.3612 2.09475 11.7206 3.7345 9.625 4.37413ZM15.75 10.4991C17.0074 10.8824 17.9917 11.8676 18.3741 13.125C18.7591 11.8676 19.7435 10.8833 21 10.5C19.7435 10.1159 18.7591 9.13237 18.3741 7.875C17.9917 9.1315 17.0074 10.1159 15.75 10.4991ZM10.5 15.3125C11.5482 15.6319 12.3681 16.4526 12.6866 17.5C13.0069 16.4517 13.8276 15.6319 14.875 15.3125C13.8276 14.9923 13.0077 14.1733 12.6866 13.125C12.3681 14.1724 11.5482 14.9923 10.5 15.3125ZM5.24913 4.375C4.4835 6.88888 2.51213 8.85938 0 9.62588C2.5165 10.3959 4.4835 12.3629 5.2535 14.8794C6.01912 12.3646 7.9905 10.3959 10.5 9.62588C7.9905 8.86112 6.01912 6.88975 5.24913 4.375Z"
                                  fill="#757575"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      )}

                      {(item?.apps?.[0]?.snapshotselected?.includes("AI Celender App") || item?.apps?.includes("AI Celender App")) && (
                        <div className="everyicon-z">
                          <div className="ml-2 mr-2 mb-2 ">
                            <div className="border-gray-300 border rounded-full h-9 w-9 p-2">
                              <svg
                                width="21"
                                height="21"
                                viewBox="0 0 21 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <mask
                                  id="mask0_391_66"
                                  style={{ maskType: "luminance" }}
                                  maskUnits="userSpaceOnUse"
                                  x="0"
                                  y="0"
                                  width="21"
                                  height="21"
                                >
                                  <path
                                    d="M20.2314 0.929688H0.231445V20.9297H20.2314V0.929688Z"
                                    fill="white"
                                  />
                                </mask>
                                <g mask="url(#mask0_391_66)">
                                  <path
                                    d="M16.0648 4.26172H4.39811C3.47764 4.26172 2.73145 5.00791 2.73145 5.92839V17.5951C2.73145 18.5155 3.47764 19.2617 4.39811 19.2617H16.0648C16.9853 19.2617 17.7314 18.5155 17.7314 17.5951V5.92839C17.7314 5.00791 16.9853 4.26172 16.0648 4.26172Z"
                                    stroke="#757575"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                  <path
                                    d="M13.5645 2.59766V5.93099"
                                    stroke="#757575"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                  <path
                                    d="M6.89844 2.59766V5.93099"
                                    stroke="#757575"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                  <path
                                    d="M2.73145 9.26172H17.7314"
                                    stroke="#757575"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </g>
                              </svg>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-20-px w-12-per text-left inter-family ">
                    {" "}
                    {item.trial_and_credit.credit_amount}
                  </div>
                  <div className="p-20-px w-12-per text-left inter-family ">
                    <Tooltip title={`${item?.yearly_id ? item?.yearly_id : ""}`} arrow>
                      <div className="d-flex cursor-pointer">
                        <h1 className="cursor-pointer inter-family font-16 text-black">
                          {getCurrencySymbol(item?.category?.crncy_symbol)}
                          {item.yearly_amount ? item.yearly_amount / 100 : 0}
                        </h1>
                        {item?.yearly_id && (
                          <div onClick={() => copyFields(item?.yearly_id)}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              width="24px"
                              height="24px"
                            >
                              <path d="M16 1h-4.18C11.4 0.42 10.74 0 10 0s-1.4.42-1.82 1H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h2v3.36c0 .49.53.77.91.5l3.64-2.6h5.45c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm-4.18 1.44c.23-.38.71-.44.88-.44.17 0 .65.06.88.44.23.37.42 1.14.42 1.56s-.2 1.19-.42 1.56c-.23.38-.71.44-.88.44-.17 0-.65-.06-.88-.44C11.2 4.19 11 3.62 11 3.44s.2-1.19.42-1.56zM14 16H6v-1.5h8V16zm0-3H6v-1.5h8V13zm0-3H6V8.5h8V10z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                  <div className="p-20-px w-12-per text-left inter-family">
                    <Tooltip title={`${item?.monthly_id ? item?.monthly_id : ""}`} arrow>
                      <div className=" cursor-pointer d-flex">
                        <h1 className="cursor-pointer inter-family font-16 text-black">
                          {getCurrencySymbol(item?.category?.crncy_symbol)}
                          {item.monthly_amount ? item.monthly_amount / 100 : 0}
                        </h1>
                        {item?.monthly_id && (
                          <div
                            className="cursor-pointer"
                            onClick={() => copyFields(item?.monthly_id)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              width="24px"
                              height="24px"
                            >
                              <path d="M16 1h-4.18C11.4 0.42 10.74 0 10 0s-1.4.42-1.82 1H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h2v3.36c0 .49.53.77.91.5l3.64-2.6h5.45c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm-4.18 1.44c.23-.38.71-.44.88-.44.17 0 .65.06.88.44.23.37.42 1.14.42 1.56s-.2 1.19-.42 1.56c-.23.38-.71.44-.88.44-.17 0-.65-.06-.88-.44C11.2 4.19 11 3.62 11 3.44s.2-1.19.42-1.56zM14 16H6v-1.5h8V16zm0-3H6v-1.5h8V13zm0-3H6V8.5h8V10z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                  <div className="p-20-px w-12-per text-left inter-family">
                    {
                      currentItems?.locations?.filter((location) => location?.product_id == item.id)
                        ?.length
                    }{" "}
                  </div>
                  {/* <div className="p-20-px text-black  text-right w-15-per">
                      <button
                        onClick={() => handleCreateAccounts(item.id)}
                        className="btn-new-design-black-addacount font-14-px"
                      >
                        Create Account
                      </button> 
                    </div>*/}
                  <div
                    className=" w-12-per text-left d-flex"
                    style={{ alignItems: "center", justifyContent: "space-between" }}
                  >
                    <Tooltip title={`Developer Plan`} arrow>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item?.developer_plan || false}
                            onChange={() => updateOnSaasToggleChange(item)}
                          />
                        }
                        label=""
                      />
                    </Tooltip>
                  </div>

                  <div
                    className=" w-12-per text-left d-flex"
                    style={{ alignItems: "center", justifyContent: "space-between" }}
                  >
                    <Tooltip title={`Show Plan On Account Level`} arrow>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item.isenable_account || false}
                            onChange={() => handleToggleChange(item)}
                          />
                        }
                        label=""
                      />
                    </Tooltip>
                  </div>
                  <div
                    className=" w-12-per text-left d-flex"
                    style={{ alignItems: "center", justifyContent: "space-between" }}
                  >
                    <Tooltip title={`Default Plan`} arrow>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item?.default_plan || false}
                            onChange={() => updateOnDefaultPlanToggleChange(item)}
                          />
                        }
                        label=""
                      />
                    </Tooltip>
                  </div>
                  <div
                    className=" w-12-per text-left d-flex"
                    style={{ alignItems: "center", justifyContent: "space-between" }}
                  >
                    <button
                      className="cursor-pointer"
                      style={{
                        background: "black",
                        color: "white",
                        paddingBlock: "10px",
                        paddingInline: "14px",
                        borderRadius: "6px",
                        fontWeight: "medium",
                      }}
                      onClick={() => {
                        handleCopyLink(item._id, 'monthly');
                      }}
                    >
                      {isCopy.id === item?._id && isCopy.type === 'monthly' ? "Copied" : "Copy Link"}
                    </button>
                  </div>
                  <div
                    className=" w-12-per text-left d-flex"
                    style={{ alignItems: "center", justifyContent: "space-between" }}
                  >
                    <button
                      className="cursor-pointer"
                      style={{
                        background: "black",
                        color: "white",
                        paddingBlock: "10px",
                        paddingInline: "14px",
                        borderRadius: "6px",
                        fontWeight: "medium",
                      }}
                      onClick={() => {
                        handleCopyLink(item._id, 'yearly');
                      }}
                    >
                      {isCopy.id === item?._id && isCopy.type === 'yearly' ? "Copied" : "Copy Link"}
                    </button>
                  </div>
                  <div
                    className=" w-12-per text-left d-flex"
                    style={{ alignItems: "center", justifyContent: "center", gap: "12px" }}
                  >
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        openeditsaasmodel(item);
                      }}
                    >
                      <Icon>edit</Icon>
                    </div>
                    {item?.number_of_subscriptions === 0 ? (
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          handleDeleteClick(item);
                        }}
                      >
                        <Icon sx={{ color: item?.number_of_subscriptions === 0 ? "#000" : "#bdc4ce" }}>delete</Icon>
                      </div>
                    ) : (
                      <Tooltip
                        title="Unable to delete a plan that has subscriptions attached to it"
                        placement="top"
                      >
                        <div style={{ cursor: "not-allowed" }}>
                          <Icon sx={{ color: item?.number_of_subscriptions === 0 ? "#000" : "#bdc4ce" }}>delete</Icon>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </>
            ))}
        </div>
      </div>

      <DeleteModal
        openSoftModel={openSoftModel}
        handleCloseModal={handleCloseModal}
        deleteConfirmationText={deleteConfirmationText}
        setDeleteConfirmationText={setDeleteConfirmationText}
        deletesaasmodel={deletesaasmodel}
        deleteLoading={deleteLoading}
      />

      {/* Pagination Controls */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          style={{
            padding: "8px 16px",
            margin: "0 5px",
            backgroundColor: "white",
            color: "black",
            border: "1px solid black",
            borderRadius: "4px",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            opacity: currentPage === 1 ? 0.6 : 1,
          }}
        >
          Previous
        </button>

        {[...Array(endPage - startPage + 1)].map((_, index) => {
          const pageNumber = startPage + index;
          return (
            <button
              key={pageNumber}
              onClick={() => handlePageChange(pageNumber)}
              style={{
                padding: "8px 16px",
                margin: "0 5px",
                backgroundColor: pageNumber === currentPage ? "black" : "white",
                color: pageNumber === currentPage ? "white" : "black",
                border: pageNumber === currentPage ? "1px solid white" : "1px solid black",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {pageNumber}
            </button>
          );
        })}

        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          style={{
            padding: "8px 16px",
            margin: "0 5px",
            backgroundColor: "white",
            color: "black",
            border: "1px solid black",
            borderRadius: "4px",
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            opacity: currentPage === totalPages ? 0.6 : 1,
          }}
        >
          Next
        </button>
      </div>
    </>
  );
};
export default NormalTable;
